import axios from 'axios';
import config from '../config';
import { createTrackedHeaders, CALL_TYPES } from '../utils/apiCallTracker';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

// Configure axios instance for Bedrock service
const bedrockServiceClient = axios.create({
    baseURL: config.api.bedrockServiceUrl,
    timeout: 300000, // 5 minutes - increased for slow AWS responses
    headers: {}
});

// Add request interceptor to include auth token
bedrockServiceClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(bedrockServiceClient);

// Proactively refresh token before any request if within 30-minute expiry window
applyTokenRefreshInterceptor(bedrockServiceClient);

export const promptService = {
    // Create a new prompt
    createPrompt: async (promptData) => {
        const response = await bedrockServiceClient.post('/bedrock/prompts', promptData, {
            headers: createTrackedHeaders(CALL_TYPES.MANUAL)
        });
        return response.data;
    },

    // Get all prompts (latest versions only)
    getPrompts: async (limit = null, lightweight = false) => {
        const params = {};
        if (limit) params.limit = limit;
        if (lightweight) params.lightweight = true;
        const response = await bedrockServiceClient.get('/bedrock/prompts', {
            params,
            headers: createTrackedHeaders(CALL_TYPES.AUTOMATIC)
        });
        return response.data;
    },

    // Get a specific prompt by prompt_id
    getPrompt: async (prompt_id, version = null) => {
        const params = version ? { version } : {};
        if (prompt_id.includes(':'))
            prompt_id = prompt_id.split(':')[0];
        const response = await bedrockServiceClient.get(`/bedrock/prompts/${prompt_id}`, {
            params,
            headers: createTrackedHeaders(CALL_TYPES.AUTOMATIC)
        });
        return response.data;
    },

    // Get all versions of a prompt
    getPromptVersions: async (prompt_id) => {
        if (prompt_id.includes(':'))
            prompt_id = prompt_id.split(':')[0];
        const response = await bedrockServiceClient.get(`/bedrock/prompts/${prompt_id}/versions`, {
            headers: createTrackedHeaders(CALL_TYPES.AUTOMATIC)
        });
        return response.data;
    },

    // Update a prompt (creates new version)
    updatePrompt: async (prompt_id, promptData) => {
        // if prompt_id contains version info, extract only the ID part
        if (prompt_id.includes(':'))
            prompt_id = prompt_id.split(':')[0];

        const response = await bedrockServiceClient.put(`/bedrock/prompts/${prompt_id}`, promptData, {
            headers: createTrackedHeaders(CALL_TYPES.MANUAL)
        });
        return response.data;
    },

    // Delete a prompt or specific version
    deletePrompt: async (prompt_id, version = null) => {
        const params = version ? { version } : {};
        if (prompt_id.includes(':'))
            prompt_id = prompt_id.split(':')[0];
        const response = await bedrockServiceClient.delete(`/bedrock/prompts/${prompt_id}`, {
            params,
            headers: createTrackedHeaders(CALL_TYPES.MANUAL)
        });
        return response.data;
    },

    // Set a specific version as default
    setDefaultVersion: async (prompt_id, version) => {
        if (prompt_id.includes(':'))
            prompt_id = prompt_id.split(':')[0];
        const response = await bedrockServiceClient.post(`/bedrock/prompts/${prompt_id}/set-default`, { version }, {
            headers: createTrackedHeaders(CALL_TYPES.MANUAL)
        });
        return response.data;
    },

    // Get the default version of a prompt
    getDefaultVersion: async (prompt_id) => {
        if (prompt_id.includes(':'))
            prompt_id = prompt_id.split(':')[0];
        const response = await bedrockServiceClient.get(`/bedrock/prompts/${prompt_id}/default`, {
            headers: createTrackedHeaders(CALL_TYPES.AUTOMATIC)
        });
        return response.data;
    }
};

export default promptService;